// pause-rhythm — how the pause row of the voice-metrics card is worded.
//
// Two surfaces render it: the panel (v2 VoiceMetricsSection) and the PDF
// (print-report/sections/voice-metrics). Both used to carry a byte-identical
// copy of the ladder and the detail string, and the last change had to edit
// them in lockstep. 7 of 13 screen/PDF mirroring tasks in this codebase have
// already drifted apart; a threshold below tweaked in one copy would make the
// printed report describe the same recording differently from the panel the
// customer had just read, with nothing failing to show it.
//
// Plain TS on purpose: the print module never imports React components.
import type { TFunction } from 'i18next';
import type { VoiceMetrics } from '@aica/api-types/schemas';

type Pauses = VoiceMetrics['pauses'];

/**
 * The acoustic service classifies rhythm itself, from the mean pause length.
 * Records written before that fix carry no `rhythm`, so they fall back to the
 * old count-based ladder — including its `speakingRatio >= 0.97` guard, which
 * existed only to mask the ratio bug (it was pinned at ~1.0 on every call) and
 * must not be applied to corrected data.
 */
export function resolvePauseRhythm(pauses: Pauses): string {
  return (
    pauses.rhythm ??
    (pauses.speakingRatio >= 0.97
      ? 'smooth'
      : pauses.count === 0
        ? 'none'
        : (pauses.longPauses ?? 0) > 2
          ? 'hesitation'
          : pauses.count > 10
            ? 'choppy'
            : 'normal')
  );
}

/**
 * Mean pause length is the measured figure; the old "N long" count was never
 * measurable from eGeMAPS functionals and always read 0. Historical records
 * keep showing it rather than an empty slot.
 */
export function formatPauseDetail(pauses: Pauses, t: TFunction): string {
  return pauses.avgPauseMs != null
    ? `${Math.round(pauses.avgPauseMs)} ${t('records.analysisPanel.voiceMetrics.msAvg', 'ms avg')}`
    : `${pauses.longPauses ?? 0} ${t('records.analysisPanel.voiceMetrics.long', 'long')}`;
}

/**
 * The pause row's VALUE — the count, the mean pause and the speaking share, or
 * the "insufficient data" wording when the read is degraded.
 *
 * The acoustic service zeroes every pause figure when its VAD fails and says so
 * with `rhythm: 'unknown'`. Both surfaces already picked the right CAPTION for
 * that case, and both went on printing the zeroed figures beside it as if they
 * were measurements: "143 · 0 ms avg · 0% speaking", directly above a sentence
 * saying there was not enough data to tell. Same string on screen and in the
 * PDF, so a customer reading both cannot be told two things.
 */
export function formatPauseValue(pauses: Pauses, t: TFunction): string {
  if (resolvePauseRhythm(pauses) === 'unknown') {
    return t('records.analysisPanel.voiceMetrics.unavailable', {
      defaultValue: 'Insufficient data',
    });
  }
  const speakingPct = Math.round(pauses.speakingRatio * 100);
  const speaking = t('records.analysisPanel.voiceMetrics.speaking', {
    defaultValue: 'speaking',
  });
  return `${pauses.count} · ${formatPauseDetail(pauses, t)} · ${speakingPct}% ${speaking}`;
}
